import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "./popup.scss";
import { IoMdClose } from "react-icons/io";
import { useEscKey } from "src/hooks/useEscKey";

export interface PopupAction {
    label: string;
    variant?: "primary" | "danger" | "write-off" | "default";
    disabled?: boolean;
    onClick: () => void;
}

interface PopupProps {
    title?: React.ReactNode;
    message: React.ReactNode;
    actions: PopupAction[];
    onClose: () => void;
    className?: string;
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Popup({ title, message, actions, onClose, className }: PopupProps) {
    const { t } = useTranslation();
    const messageRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const titleId = useRef(`popup-title-${Math.random().toString(36).slice(2)}`).current;
    const [needsScroll, setNeedsScroll] = useState(false);

    useEscKey(onClose, true);

    useLayoutEffect(() => {
        const el = messageRef.current;
        if (!el) return;

        const update = () => {
            setNeedsScroll(el.scrollHeight > el.clientHeight + 1);
        };

        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [message]);

    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const dialog = dialogRef.current;
        const firstFocusable = dialog?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (firstFocusable ?? dialog)?.focus();

        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== "Tab" || !dialog) return;
            const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleTab);
        return () => {
            document.removeEventListener("keydown", handleTab);
            previouslyFocused?.focus();
        };
    }, []);

    return createPortal(
        <div className="popup-overlay" role="presentation" onClick={onClose}>
            <div
                ref={dialogRef}
                className={`popup${className ? ` ${className}` : ""}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="popup__header">
                    <span className="popup__title" id={titleId}>{title ?? "Confirm"}</span>
                    <button className="popup__close" onClick={onClose} aria-label={t("popup.close")}>
                        <IoMdClose />
                    </button>
                </div>
                <div
                    ref={messageRef}
                    className={`popup__message${needsScroll ? " popup__message--scroll" : ""}`}
                >
                    {message}
                </div>
                <div className="popup__actions">
                    {actions.map((a) => (
                        <button
                            key={a.label}
                            className={`popup__btn popup__btn--${a.variant ?? "default"}`}
                            onClick={a.onClick}
                            disabled={a.disabled}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
}
